import React, { ReactElement } from 'react';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import useAuth from '../auth/authContext';
import { AppRoute, AppRoutes } from '../router/AppRoutes';
import VisibleResource from './VisibleResource';
import {
  AiStarsIcon,
  BriefcaseIcon,
  BulbIcon,
  CalendarIcon,
  LogoGrowegy,
  PowerIcon,
  QuestionMarkIcon,
  SettingsIcon,
  ShoppingBagIcon,
} from '../assets/icons';
import clsx from 'clsx';
import {
  useIsNewVersionPersonalRecommendations,
  usePersonalRecommendations,
} from '../recommendations/hooks/usePersonalRecommendations';
import { ICurrentUser } from '../currentuser/currentUser';
import { Permission } from '../auth/permissions';
import { businessPlanConfig, growegyAIConfig, marketplaceConfig } from '../commons/config';
import { StyleVariables } from '../commons/styleConstants';
import { SanityChecker } from '../meta/components/SanityChecker';
import { useHasFetchedPastDueInvoices } from '../billing/hooks/useFetchedBillingInvoices';
import { useIsSmallerThanTablet, useIsTabletOrLarger } from '../responsive/hooks/hooks';
import { GrowegyMenu } from '../commons/components/GrowegyMenu';
import useAnalytics from '../web-analytics/webAnalyticsContext';
import { UserAvatar } from '../commons/components/UserAvatar';
import { useCurrentUser } from '../user/hooks/useCurrentUser';
import { extractUserName } from '../user/utils';
import { useNewReferrals } from '../marketplace/hooks/useNewReferrals';

import { OnStartModalPrompt } from '../commons/components/OnStartModalPrompt';
import { createTutorialsMenuItem } from '../help/components/TutorialMenuItem';
import { createReleasesMenuItem } from '../help/components/ReleaseMenuItem';
import { createSupportMenuItem } from '../help/components/SupportMenuItem';
import { HelpMenu } from '../help/components/HelpMenu';
import { StickyNote } from './StickyNote';
import { useAppSelector } from '../app/hooks';
import { useUpdateOnboardingProgressMutation } from '../onboarding/hooks/useUpdateOnboardingProgressMutation';
import { useFetchedOnboardingProgress } from '../onboarding/hooks/useFetchedOnboardingProgress';
import { ONBOARDING_STATE_NOT_OFFERED, ONBOARDING_STATE_NOT_STARTED } from '../user/userApi';
import { restartOnboardingMenuItem } from '../help/components/RestartOnboardingMenuItem';
import { analyticsTrack } from '../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../web-analytics/AnalyticsEvent';

const SideBarItem = (props: {
  header: string;
  icon: (props: { fill: string }) => ReactElement;
  isActive: boolean;
  subheader?: string;
  badge?: string | null;
}) => {
  const { header, icon, isActive, subheader, badge } = props;
  const color = isActive ? StyleVariables.neutral900 : StyleVariables.neutral500;
  return (
    <div className="app-sidebar__item">
      <div
        className={clsx('app-sidebar__item-icon', { active: isActive })}
        data-test="app-sidebar__item-icon"
      >
        {icon({ fill: color })}
        {badge && (
          <div className="app-sidebar__item-indicator-container">
            <div className="app-sidebar__item-indicator">
              <span
                className="app-sidebar__item-indicator-number"
                data-test="app-sidebar__badge-value"
              >
                {badge}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="app-sidebar__item-header" data-test="side-bar__link-header" style={{ color }}>
        {header}
      </div>
      {subheader ? <div className="app-sidebar__item-badge">{subheader}</div> : null}
    </div>
  );
};

const SideBarLink = (props: {
  to: AppRoute;
  alternativeTo?: AppRoute;
  secondAlternativeTo?: AppRoute;
  header: string;
  icon: (props: { fill: string }) => ReactElement;
  subheader?: string;
  badge?: string | null;
  stickyNoteContent?: ReactElement | null;
}) => {
  const {
    to,
    alternativeTo,
    secondAlternativeTo,
    header,
    icon,
    subheader,
    badge,
    stickyNoteContent,
  } = props;
  const isPrimaryActive = useMatch(to)!!;
  const isAlternativeActive = useMatch(alternativeTo ?? 'not-matching-path');
  const isSecondAlternativeToActive = useMatch(secondAlternativeTo ?? 'not-matching-path');
  const isActive = !!(
    isPrimaryActive ||
    (alternativeTo && isAlternativeActive) ||
    (secondAlternativeTo && isSecondAlternativeToActive)
  );
  return (
    <NavLink className="side-bar__link" data-test="side-bar__link" to={to}>
      <SideBarItem
        header={header}
        icon={icon}
        isActive={isActive}
        subheader={subheader}
        badge={badge}
      />
      {stickyNoteContent && <StickyNote>{stickyNoteContent}</StickyNote>}
    </NavLink>
  );
};

const SideBarLogo = () => (
  <div className="app-sidebar__logo" data-test="app-sidebar__logo">
    <img src={LogoGrowegy} alt="" />
  </div>
);

const SideBarPlanning = () => {
  return (
    <VisibleResource
      to={AppRoute.calendar}
      render={(to) => (
        <SideBarLink
          to={to}
          header="Planning"
          icon={(props: { fill: string }) => <CalendarIcon fill={props.fill} />}
        />
      )}
    />
  );
};

const SideBarBusinessPlan = () => {
  return (
    <VisibleResource
      to={AppRoute.businessPlan}
      render={(to) => (
        <SideBarLink
          to={to}
          header="Business Plan"
          icon={(props: { fill: string }) => <BriefcaseIcon fill={props.fill} />}
        />
      )}
    />
  );
};

const SideBarGrowegyAI = (props: { user: ICurrentUser }) => {
  const { user } = props;
  const { justCompleted } = useAppSelector((state) => state.onboarding);
  if (!growegyAIConfig.enabled || !user.has(Permission.USE_CHAT_GPT)) return null;

  return (
    <VisibleResource
      to={AppRoutes.Home}
      render={(to) => (
        <SideBarLink
          to={to}
          alternativeTo={AppRoutes.GrowegyAI}
          header="Growegy AI"
          icon={(props: { fill: string }) => <AiStarsIcon fill={props.fill} />}
          stickyNoteContent={
            justCompleted ? (
              <div>
                Thanks for completing onboarding.
                <br />
                You are currently here.
              </div>
            ) : null
          }
        />
      )}
    />
  );
};

const SideBarRecommendations = (props: { user: ICurrentUser }) => {
  const { user } = props;
  const hasNewRecommendations = useIsNewVersionPersonalRecommendations();
  const { data: recommendations } = usePersonalRecommendations('');

  if (
    !user.has(Permission.GET_TENANT_RECOMMENDATIONS) ||
    !recommendations ||
    recommendations.recommendations.length === 0
  )
    return null;

  return (
    <VisibleResource
      to={AppRoutes.Recommendations}
      render={(to) => (
        <SideBarLink
          to={to}
          header="Insights"
          icon={(props: { fill: string }) => <BulbIcon fill={props.fill} />}
          badge={hasNewRecommendations ? '1' : null}
        />
      )}
    />
  );
};

const SideBarMarketplace = () => {
  const { newReferrals } = useNewReferrals();
  const isSmallerThanTablet = useIsSmallerThanTablet();
  return (
    <VisibleResource
      to={AppRoutes.Marketplace}
      render={(to) => (
        <SideBarLink
          to={to}
          header={isSmallerThanTablet ? 'Market' : 'Marketplace'}
          icon={(props: { fill: string }) => <ShoppingBagIcon fill={props.fill} />}
          badge={newReferrals.length > 0 ? `${Math.min(newReferrals.length, 99)}` : null}
        />
      )}
    />
  );
};

const SideBarSettings = () => {
  const hasPastDueInvoices = useHasFetchedPastDueInvoices();
  return (
    <VisibleResource
      to={AppRoutes.Settings}
      render={(to) => (
        <SideBarLink
          to={to}
          secondAlternativeTo={AppRoute.settings}
          alternativeTo={AppRoute.billing}
          header="Settings"
          icon={(props: { fill: string }) => <SettingsIcon fill={props.fill} />}
          badge={hasPastDueInvoices ? '1' : null}
        />
      )}
    />
  );
};

const SideBarHelp = () => {
  return (
    <VisibleResource
      to={AppRoute.home}
      render={() => {
        return (
          <HelpMenu
            customToggle={() => (
              <SideBarItem
                header="Help"
                icon={(props: { fill: string }) => <QuestionMarkIcon fill={props.fill} />}
                isActive={false}
              />
            )}
          />
        );
      }}
    />
  );
};

const SideBarNavigation = (props: { user: ICurrentUser }) => {
  const { user } = props;
  const isTabletOrLarger = useIsTabletOrLarger();

  return (
    <nav className="app-sidebar__navigation">
      <div className="app-sidebar__navigation-items">
        {isTabletOrLarger ? (
          <>
            <SideBarGrowegyAI user={user} />
            <SideBarPlanning />
            {businessPlanConfig.enabled && <SideBarBusinessPlan />}
            <SideBarRecommendations user={user} />
            {marketplaceConfig.enabled && <SideBarMarketplace />}
            <SideBarSettings />
            <SideBarHelp />
          </>
        ) : (
          <>
            <SideBarGrowegyAI user={user} />
            <SideBarPlanning />
            {businessPlanConfig.enabled && <SideBarBusinessPlan />}
            {marketplaceConfig.enabled && <SideBarMarketplace />}
            <SideBarSettings />
            <SideBarAccount />
          </>
        )}
      </div>
    </nav>
  );
};

const SideBarAccount = () => {
  const isTabletOrLarger = useIsTabletOrLarger();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const {
    query: { data: onboardingProgress },
  } = useFetchedOnboardingProgress();

  const { mutate: updateOnboardingProgress } = useUpdateOnboardingProgressMutation();
  const onboardingCouldBeRestarted =
    !!onboardingProgress &&
    onboardingProgress.state !== ONBOARDING_STATE_NOT_OFFERED &&
    onboardingProgress.state !== ONBOARDING_STATE_NOT_STARTED;

  return (
    <VisibleResource
      to={AppRoute.home}
      render={() => (
        <GrowegyMenu
          menuItems={[
            {
              type: 'custom',
              dataTest: 'app-sidebar__account-container',
              component: () => (
                <div className="app-sidebar__account-container">
                  <div className="app-sidebar__account-info-container">
                    <span>{extractUserName(user)}</span>
                    <span className="app-sidebar__account-role">{user.role.name}</span>
                  </div>
                  <div className="app-sidebar__account-email">{user.email}</div>
                </div>
              ),
            },
            {
              type: 'divider',
              dataTest: 'app-sidebar__account-container-divider',
            },
            restartOnboardingMenuItem(!onboardingCouldBeRestarted, () => {
              analyticsTrack(analytics, AnalyticsEvent.ONBOARDING_RESTARTED);
              updateOnboardingProgress({
                state: ONBOARDING_STATE_NOT_STARTED,
                steps: [],
              });
            }),
            createTutorialsMenuItem(isTabletOrLarger, analytics, 'app-sidebar__account-tutorials'),
            createReleasesMenuItem(isTabletOrLarger, analytics, 'app-sidebar__account-releases'),
            createSupportMenuItem(isTabletOrLarger, 'app-sidebar__account-support'),
            {
              type: 'divider',
              isHidden: isTabletOrLarger,
              dataTest: 'app-sidebar__account-sign-out-divider',
            },
            {
              type: 'standard',
              text: 'Sign out',
              icon: <PowerIcon />,
              dataTest: 'app-sidebar__account-sign-out',
              onClick: () => navigate(AppRoute.signOut),
            },
          ]}
          menuButtonDataTest="side-bar__link-button"
          containerDataTest="side-bar__link"
          containerClassName="side-bar__link"
          drop={isTabletOrLarger ? 'right' : 'up'}
          positionStrategy="fixed"
          customToggle={() => (
            <SideBarItem
              header="Account"
              icon={() => <UserAvatar user={user} />}
              isActive={false}
            />
          )}
        />
      )}
    />
  );
};

export const SideBar = () => {
  const {
    state: { user },
  } = useAuth();
  const isTabletOrLarger = useIsTabletOrLarger();

  return (
    user && (
      <div className="app-sidebar" data-test="app-sidebar">
        <SideBarLogo />
        <SideBarNavigation user={user} />
        {isTabletOrLarger && (
          <div className="app-sidebar__navigation-items" style={{ flex: '0 0 auto' }}>
            <SideBarAccount />
          </div>
        )}
        <SanityChecker />
        <OnStartModalPrompt />
      </div>
    )
  );
};
